import React from 'react';
import firebase from 'firebase';
import { useFirebase } from './context';
import { MenuHeaderState } from '../Menu/model/MenuHeader';
import { SaveableDiscountUsage } from '../Discount/hooks';
import { WithSourceId } from './custom_types';
import { DiscountItem, WithMetadata } from 'widok-bridge';
import { LocalDate, Instant, convert } from '@js-joda/core';

export function useFirestoreDb() {
    const fb = useFirebase();
    const [db] = React.useState(new FirestoreDb(fb.firestore, fb.database));
    return db;
}

interface GetDbRootParam {
    useProd?: boolean;
}

enum DbEnv {
    PROD = 1,
    TEST = 2,
}


class DiscountUsagesConverter implements firebase.firestore.FirestoreDataConverter<SaveableDiscountUsage> {
    toFirestore(obj: SaveableDiscountUsage): firebase.firestore.DocumentData {
        return obj;
    }
    fromFirestore(snapshot: firebase.firestore.QueryDocumentSnapshot): SaveableDiscountUsage {
        const data = snapshot.data();
        const result: SaveableDiscountUsage = { ...data }
        return result;
    }
}

export class FirestoreDb {
    private firestore: firebase.firestore.Firestore;
    private database: firebase.database.Database;

    constructor(firestore: firebase.firestore.Firestore,
        database: firebase.database.Database) {
        this.firestore = firestore;
        this.database = database;
    }

    getDbEnv(param?: GetDbRootParam) {
        const isProdEnv = process.env.REACT_APP_ENV === 'prod';
        const useProd = (isProdEnv && (param?.useProd !== false))
            || (!isProdEnv && (param?.useProd === true));
        return useProd
            ? DbEnv.PROD
            : DbEnv.TEST;
    }

    getRootAndEnv(param?: GetDbRootParam) {
        const dbEnv = this.getDbEnv(param);
        if (dbEnv === DbEnv.PROD) {
            return {
                db: this.firestore,
                env: DbEnv.PROD,
            }
        }
        return {
            db: this.firestore.collection('test').doc('db'),
            env: DbEnv.TEST,
        };
    }

    public getRoot(param?: GetDbRootParam) {
        return this.getRootAndEnv(param).db;
    }

    getMenuHeaderRoot(param?: GetDbRootParam): firebase.firestore.DocumentReference<{ data: MenuHeaderState }> {
        return this.getRoot(param).collection('configs').doc('menuHeader').withConverter(new MenuHeaderConverter());
    }

    getRawMenuRef() {
        return this.database.ref(`public/menu/raw`);
    }

    getDiscountUsageRootBySourceId(sourceId: string): firebase.firestore.CollectionReference<SaveableDiscountUsage> {
        const dbEnv = this.getDbEnv();
        const collectionGroupName = (dbEnv === DbEnv.PROD) ? 'discount_usages' : 'test_discount_usages';
        return this.getRoot().collection('discounts').doc(sourceId).collection(collectionGroupName)
            .withConverter(new DiscountUsagesConverter());
    }


    getDiscountsRoot(): firebase.firestore.CollectionReference<WithSourceId<DiscountItem>> {
        return this.getRoot().collection('discounts')
            .withConverter(
                new WithSourceIdConverter(
                    new WithMetadataConverter(
                        new DeepLocalDateConverterNew(
                            new DiscountsConverter()))));
    }
}

class MenuHeaderConverter implements firebase.firestore.FirestoreDataConverter<{ data: MenuHeaderState }> {
    toFirestore(obj: { data: MenuHeaderState }): firebase.firestore.DocumentData {
        return obj;
    }
    fromFirestore(snapshot: firebase.firestore.QueryDocumentSnapshot): { data: MenuHeaderState } {
        const result: { data: MenuHeaderState } = { data: {} };
        const data = snapshot.data();
        if ('data' in data) {
            result.data = (data.data) as any;
        }
        return result;
    }
}

class WithSourceIdConverter<T>
    implements firebase.firestore.FirestoreDataConverter<WithSourceId<T>> {
    constructor(private converter: WithMetadataConverter<T>) { }

    toFirestore(obj: WithSourceId<T>): firebase.firestore.DocumentData {
        return {
            sourceId: obj.sourceId,
            ...this.converter.toFirestore(obj),
        }
    }
    fromFirestore(snapshot: firebase.firestore.QueryDocumentSnapshot): WithSourceId<T> {
        const data = snapshot.data();
        return {
            sourceId: data.sourceId,
            ...this.converter.fromFirestore(snapshot),
        };
    }
};

interface Converter<T> {
    toFirestore(t: T): firebase.firestore.DocumentData;
    fromFirestore(t: firebase.firestore.DocumentData): T;
}

export class WithMetadataConverter<T> implements firebase.firestore.FirestoreDataConverter<WithMetadata<T>> {
    constructor(private converter: Converter<T>) { }

    toFirestore(obj: WithMetadata<T>) {
        return this.converter.toFirestore(obj.obj);
    }
    fromFirestore(data: firebase.firestore.QueryDocumentSnapshot): WithMetadata<T> {
        const obj = this.converter.fromFirestore(data);
        return {
            obj: obj,
        };
    }
};


type SubstituteLocalDate<T> = SubstituteType<T, LocalDate, firebase.firestore.Timestamp>;
type SubstituteTimestamp<T> = SubstituteType<T, firebase.firestore.Timestamp, LocalDate>;

type SubstituteType<T, A, B> =
    T extends A
    ? B
    : T extends {}
    ? { [K in keyof T]: SubstituteType<T[K], A, B> }
    : T;

function superReplace<T, From, To>(
    obj: T, matcher: any, replacer: (t: From) => To)
    : SubstituteType<T, From, To> {
    if (obj === undefined) {
        return undefined as any;
    } else if (obj === null) {
        return null as any;
    } else if (matcher(obj)) {
        return replacer(obj as any) as any;
    } else if (Array.isArray(obj)) {
        return obj.map(element => superReplace(element, matcher, replacer)) as any;
    } else if (typeof obj === 'object') {
        const replace: any = {};
        for (const prop in obj) {
            replace[prop] = superReplace(obj[prop], matcher, replacer);
        }
        return replace;
    }
    return obj as any;
}

function superReplaceLocalDate<T>(orig: T): SubstituteLocalDate<T> {
    return superReplace<T, LocalDate, firebase.firestore.Timestamp>(
        orig, (t: any) => (t instanceof LocalDate), d => toTimestamp(d));
};

function superReplaceFirestoreTimestamp<T>(orig: T): SubstituteTimestamp<T> {
    return superReplace<T, firebase.firestore.Timestamp, LocalDate>(
        orig,
        (t: any) => (t.hasOwnProperty('seconds') && typeof t.seconds === 'number')
        , d => toLocalDate(d));
};

function toLocalDate(timestamp: firebase.firestore.Timestamp): LocalDate {
    return LocalDate.ofInstant(Instant.ofEpochSecond(timestamp.seconds))
}

export function toTimestamp(date: LocalDate) {
    const res = firebase.firestore.Timestamp.fromMillis(
        convert(date).toEpochMilli());
    return res;
}

export class DeepLocalDateConverterNew<T extends {}> implements firebase.firestore.FirestoreDataConverter<T> {
    constructor(private converter: Converter<T>) { }

    toFirestore(obj: T): firebase.firestore.DocumentData {
        return superReplaceLocalDate(this.converter.toFirestore(obj));
    }
    fromFirestore(snapshot: firebase.firestore.QueryDocumentSnapshot): T {
        const data = this.converter.fromFirestore(snapshot);
        // TODO(eryk): Try to get rid of this cast to any and change return type
        // of this converter to Converter<LocalDateRemoved<T>>
        return superReplaceFirestoreTimestamp(data) as any;
    }
}

class DiscountsConverter implements firebase.firestore.FirestoreDataConverter<DiscountItem> {
    toFirestore(obj: DiscountItem): firebase.firestore.DocumentData {
        return obj;
    }
    fromFirestore(snapshot: firebase.firestore.QueryDocumentSnapshot): DiscountItem {
        const data = snapshot.data();
        const result: DiscountItem = { ...data }
        return result;
    }
}